import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Bell, Check, CheckCheck, Loader2, MessageSquare, CreditCard, FileText, AlertCircle, Trash2, GitMerge, UserCheck } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { landlordAPI } from '../../lib/api';

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [merging, setMerging] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadUnreadCount();
    // Refresh count every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    // Instant sync when a notifications page changes read/delete state
    window.addEventListener('notifications-updated', loadUnreadCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-updated', loadUnreadCount);
    };
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationAPI.getAll();
      setNotifications(response.data.slice(0, 5)); // Show only 5 most recent
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      loadNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate(`/${user?.role}/notifications`);
  };

  const handleMarkAllAsRead = async () => {
    try {
      setClearing(true);
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark notifications as read');
    } finally {
      setClearing(false);
    }
  };

  const handleClearAll = async () => {
    try {
      setClearing(true);
      // Delete all notifications
      for (const notification of notifications) {
        await notificationAPI.deleteNotification(notification.notification_id);
      }
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error('Failed to clear notifications');
    } finally {
      setClearing(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'payment':
        return <CreditCard className="w-4 h-4 text-green-500" />;
      case 'lease':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'message':
        return <MessageSquare className="w-4 h-4 text-purple-500" />;
      case 'manual_tenant_match':
        return <GitMerge className="w-4 h-4 text-amber-500" />;
      case 'tenant_registration':
        return <UserCheck className="w-4 h-4 text-cyan-500" />;
      case 'landlord_registration':
        return <UserCheck className="w-4 h-4 text-emerald-500" />;
      case 'subscription_purchase':
      case 'subscription_refund':
      case 'landlord_trial_expired':
      case 'landlord_trial_ending':
      case 'trial_ending':
      case 'trial_expired':
        return <CreditCard className="w-4 h-4 text-amber-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
    }
  };

  const handleMerge = async (notification, e) => {
    e.stopPropagation();
    const meta = notification.metadata;
    if (!meta?.manual_tenant_id || !meta?.registered_tenant_id) return;
    setMerging(notification.notification_id);
    try {
      const res = await landlordAPI.mergeTenants({
        manual_tenant_id: meta.manual_tenant_id,
        registered_tenant_id: meta.registered_tenant_id
      });
      toast.success(res.data.message);
      await notificationAPI.markAsRead(notification.notification_id);
      loadNotifications();
      loadUnreadCount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to merge records');
    } finally {
      setMerging(null);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggle}
        className="relative"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <Badge 
            className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center p-0 text-xs bg-[#C85A42] text-white border-2 border-background"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-card rounded-lg shadow-lg border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-3 border-b bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {unreadCount} new
                </Badge>
              )}
            </div>
            {notifications.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-xs"
                  onClick={handleMarkAllAsRead}
                  disabled={clearing || unreadCount === 0}
                >
                  {clearing ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <CheckCheck className="w-3 h-3 mr-1" />
                      Mark All Read
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-xs text-destructive hover:text-destructive"
                  onClick={handleClearAll}
                  disabled={clearing}
                >
                  {clearing ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear All
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.notification_id}
                    className={cn(
                      'p-3 hover:bg-muted/50 cursor-pointer transition-colors',
                      !notification.read && 'bg-blue-50/50 dark:bg-blue-950/20'
                    )}
                    onClick={() => {
                      if (!notification.read) {
                        handleMarkAsRead(notification.notification_id, { stopPropagation: () => {} });
                      }
                    }}
                    data-testid={`notification-item-${notification.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className="mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          'text-sm line-clamp-2',
                          !notification.read && 'font-medium'
                        )}>
                          {notification.message}
                        </p>
                        {notification.type === 'manual_tenant_match' && notification.metadata && (
                          <Button
                            size="sm"
                            className="mt-1.5 h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={(e) => handleMerge(notification, e)}
                            disabled={merging === notification.notification_id}
                            data-testid={`merge-tenant-btn-${notification.notification_id}`}
                          >
                            {merging === notification.notification_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <GitMerge className="w-3 h-3 mr-1" />}
                            Merge Records
                          </Button>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDateTime(notification.created_at)}
                        </p>
                      </div>
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={(e) => handleMarkAsRead(notification.notification_id, e)}
                        >
                          <Check className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-2 border-t bg-muted/30">
            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={handleViewAll}
              data-testid="view-all-notifications"
            >
              View All Notifications
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
